import React from "react";
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
} from "react-native";
import { HorizontalScrollView } from "../ui/HorizontalScrollView";
import { Feather } from "@expo/vector-icons";
import { Footprints, Shirt, Glasses, Briefcase, LayoutGrid } from "lucide-react-native";
import type { Icon } from "@expo/vector-icons/build/createIconSet";
import { spacing } from "../../theme";
import { useColors, ThemeColors } from "../../hooks/useColors";

export interface Category {
  id: string;
  label: string;
  icon: keyof typeof Feather.glyphMap;
  lucideIcon?: "footprints" | "shirt" | "glasses" | "briefcase" | "layout-grid";
  filter_category: string;
}

export const CATEGORIES: Category[] = [
  { id: "all", label: "All", icon: "grid", lucideIcon: "layout-grid", filter_category: "" },
  {
    id: "clothing",
    label: "Clothing",
    icon: "shopping-bag", // fallback for type safety
    lucideIcon: "shirt",
    filter_category: "clothing",
  },
  { 
    id: "shoes", 
    label: "Shoes", 
    icon: "box", // fallback for type safety
    lucideIcon: "footprints",
    filter_category: "shoes" 
  },
  {
    id: "accessories",
    label: "Accessories",
    icon: "watch", // fallback for type safety
    lucideIcon: "glasses",
    filter_category: "accessories",
  },
  { id: "bags", label: "Bags", icon: "shopping-bag", lucideIcon: "briefcase", filter_category: "bags" },
];

interface ProductCategoryListProps {
  selectedCategory: string;
  onSelectCategory: (category: string) => void;
  onFilterCategorySelect?: (filterCategory: string | undefined) => void;
}

export function ProductCategoryList({
  selectedCategory,
  onSelectCategory,
  onFilterCategorySelect,
}: ProductCategoryListProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const handleCategoryPress = (category: Category) => {
    onSelectCategory(category.id);

    // If a filter category handler is provided, call it with the filter_category value
    if (onFilterCategorySelect) {
      if (category.id === "all") {
        // Clear all category filters
        onFilterCategorySelect(undefined);
      } else {
        // Set the category_subcategory filter directly
        onFilterCategorySelect(category.filter_category);
      }
    }
  };

  return (
    <HorizontalScrollView
      contentContainerStyle={styles.container}
      persistId="product-category-list"
    >
      {CATEGORIES.map((c) => {
        const selected = c.id === selectedCategory;
        return (
          <TouchableOpacity
            key={c.id}
            style={[
              styles.pill,
              selected && { backgroundColor: colors.primary },
            ]}
            onPress={() => handleCategoryPress(c)}
          >
            {c.lucideIcon === "layout-grid" ? (
              <LayoutGrid
                size={18}
                color={selected ? colors.background : "white"}
              />
            ) : c.lucideIcon === "shirt" ? (
              <Shirt
                size={18}
                color={selected ? colors.background : "white"}
              />
            ) : c.lucideIcon === "footprints" ? (
              <Footprints
                size={18}
                color={selected ? colors.background : "white"}
              />
            ) : c.lucideIcon === "glasses" ? (
              <Glasses
                size={18}
                color={selected ? colors.background : "white"}
              />
            ) : c.lucideIcon === "briefcase" ? (
              <Briefcase
                size={18}
                color={selected ? colors.background : "white"}
              />
            ) : (
            <Feather
              name={c.icon}
              size={18}
              color={selected ? colors.background : "white"}
            />
            )}
            {c.label ? (
              <Text style={[styles.label, selected && { color: colors.background }]}>
                {c.label}
              </Text>
            ) : null}
          </TouchableOpacity>
        );
      })}
    </HorizontalScrollView>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  category: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: 140,
    height: 44,
    borderRadius: 9999,
    backgroundColor: colors.backgroundSecondary,
    marginRight: 12,
  },
  selectedCategory: {
    backgroundColor: colors.primary,
  },
  selectedLabel: {
    color: "#fff",
  },
  container: {
    paddingLeft: spacing.lg,
    paddingRight: spacing.lg,
    paddingBottom: spacing.sm,
    gap: 10,
  },
  pill: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 16,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  label: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: "600",
    color: colors.text,
  },
});
